import React, { useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import classes from './NgzAddScanManualModal.module.scss';
import { LbBigModal } from '../../../shared/components/modals';
import { AddScanManualForm } from '../AddScanManualForm/AddScanManualForm';
import {
  INBODY_TYPES,
  InbodyType,
} from '../../../shared/constants/inbody-info/InbodyType';
import { useScanConfig } from './hooks/useScanConfig';
import { AddScanManualFormValues } from '../AddScanManualForm/AddScanManualForm.settings';
import { processCreateInbodyScan } from '../../utils/processCreateInbodyScan/processCreateInbodyScan';
import { angularizeWithProviders } from '../../../shared/providers';

type AddScanManualModalProps = {
  inBodyType: InbodyType;
  entityId: number;
  customerId: number;
  isAutomaticScanFallback: boolean;
  onClose: () => void;
  onComplete: () => void;
};

const NgzAddScanManualModal = ({
  inBodyType,
  entityId,
  customerId,
  isAutomaticScanFallback,
  onClose,
  onComplete,
}: AddScanManualModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formMethods = useForm<AddScanManualFormValues>({
    shouldUnregister: true,
  });

  const { handleSubmit } = formMethods;

  const { addScanConfig, scanConfigLoading } = useScanConfig({
    inBodyType,
    entityId,
  });

  const inBodyLabels = {
    modalTitle: isAutomaticScanFallback
      ? 'Cloud connection not available'
      : 'Add new scan manually',
    modalDescription: isAutomaticScanFallback
      ? 'We can not connect to your InBody ID. You will need to enter details manually to proceed the scan.'
      : null,
    scanDateTitle: 'Scan Date',
    scanTimeTitle: 'Scan Time',
  };

  const nonInBodyLabels = {
    modalTitle: 'Add Check-in',
    modalDescription: null,
    scanDateTitle: 'Check-in-date',
    scanTimeTitle: 'Check-in-time',
  };

  const labels = inBodyType === INBODY_TYPES.NON_INBODY ? nonInBodyLabels : inBodyLabels;

  const onSubmit = handleSubmit(async ({ scanData }) => {
    try {
      setIsLoading(true);
      await processCreateInbodyScan(customerId, inBodyType, scanData);
      onComplete();
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <LbBigModal
      open
      title={labels.modalTitle}
      description={labels.modalDescription}
      isLoading={isLoading || scanConfigLoading}
      buttonOptions={{
        noText: 'Cancel',
        noHandler: onClose,
        yesText: 'Save',
        yesHandler: onSubmit,
      }}
    >
      <FormProvider {...formMethods}>
        <div className={classes.container}>
          {addScanConfig && (
            <AddScanManualForm
              entryFieldConfig={addScanConfig}
              scanDateTitle={labels.scanDateTitle}
              scanTimeTitle={labels.scanTimeTitle}
            />
          )}
        </div>
      </FormProvider>
    </LbBigModal>
  );
};

angularizeWithProviders(NgzAddScanManualModal, 'ngzAddScanManualModal', {
  inBodyType: '<',
  entityId: '<',
  customerId: '<',
  isAutomaticScanFallback: '<',
  onClose: '&',
  onComplete: '&',
});
